import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Chip,
  LinearProgress,
  Avatar,
  IconButton,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  Lock as LockedIcon,
  LockOpen as UnlockedIcon,
  Event as EventIcon,
  EmojiEvents as TournamentIcon,
  SportsEsports as GameIcon,
  Speed as TrackManiaIcon,
  GolfCourse as SkillIcon,
  Group as SocialIcon
} from '@mui/icons-material';

// Rarity colors matching WoW theme
const RARITY_COLORS = {
  common: '#9d9d9d',
  rare: '#0070dd',
  epic: '#a335ee',
  legendary: '#ff8000'
};

// Category icons
const CATEGORY_ICONS = {
  events: EventIcon,
  tournaments: TournamentIcon,
  games: GameIcon,
  trackmania: TrackManiaIcon,
  skill: SkillIcon,
  social: SocialIcon
};

const AchievementModal = ({ achievement, open, onClose }) => {
  if (!achievement) return null;

  const {
    title,
    description,
    category,
    rarity,
    points,
    unlocked,
    progress = 0,
    current_value = 0,
    criteria_value,
    criteria_type,
    unlocked_at,
    awarded_by
  } = achievement;

  const rarityColor = RARITY_COLORS[rarity] || RARITY_COLORS.common;
  const CategoryIcon = CATEGORY_ICONS[category] || SocialIcon;

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCriteriaDescription = () => {
    const criteriaDescriptions = {
      event_attendance: 'Attend events',
      tournament_participation: 'Participate in tournaments',
      tournament_wins: 'Win tournaments',
      unique_games_played: 'Play different games',
      teams_created: 'Create teams',
      bar_spending: 'Spend at the bar',
      '1v1_wins': 'Win 1v1 matches',
      team_match_wins: 'Win team matches',
      putt_3m: 'Make 3m putts',
      putt_5m: 'Make 5m putts',
      putt_10m: 'Make 10m putts',
      putt_15m: 'Make 15m putts',
      trackmania_gold: 'Earn gold medals on TrackMania map'
    };
    
    return criteriaDescriptions[criteria_type] || criteria_type;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          border: `3px solid ${rarityColor}`,
          borderRadius: 2
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: `${rarityColor}15`,
          borderBottom: `2px solid ${rarityColor}`
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{
              bgcolor: rarityColor,
              width: 64,
              height: 64
            }}
          >
            <CategoryIcon sx={{ fontSize: 36 }} />
          </Avatar>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: rarityColor }}>
              {title}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
              <Chip
                label={rarity.toUpperCase()}
                size="small"
                sx={{
                  bgcolor: rarityColor,
                  color: 'white',
                  fontWeight: 'bold'
                }}
              />
              <Chip
                label={category.toUpperCase()}
                size="small"
                variant="outlined"
              />
            </Box>
          </Box>
        </Box>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        {/* Status */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 3,
            p: 2,
            bgcolor: unlocked ? 'success.light' : 'action.hover',
            borderRadius: 2
          }}
        >
          {unlocked ? (
            <UnlockedIcon sx={{ fontSize: 32, color: 'success.main' }} />
          ) : (
            <LockedIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
          )}
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {unlocked ? 'Achievement Unlocked!' : 'Locked'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {points} Achievement Points
            </Typography>
          </Box>
        </Box>

        {/* Description */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Description
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {description}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Criteria */}
        {criteria_value && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Requirements
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {getCriteriaDescription()}: {criteria_value}
            </Typography>
          </Box>
        )}

        {/* Progress (for locked achievements) */}
        {!unlocked && criteria_value && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Your Progress
            </Typography>
            <Box sx={{ mb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {current_value} / {criteria_value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {progress}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  backgroundColor: 'rgba(0,0,0,0.1)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: rarityColor
                  }
                }}
              />
            </Box>
          </Box>
        )}

        {/* Unlock information */}
        {unlocked && (
          <Box sx={{ mb: 2 }}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Unlock Information
            </Typography>
            {unlocked_at && (
              <Typography variant="body2" color="text.secondary">
                Unlocked on: {formatDate(unlocked_at)}
              </Typography>
            )}
            {awarded_by && (
              <Typography variant="body2" color="text.secondary">
                Awarded by administrator
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AchievementModal;

